import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Heart, 
  Smile, 
  ShoppingBag, 
  Fuel, 
  Gift,
  Users,
  Clock,
  FileText,
  CheckCircle,
  DollarSign,
  Calendar,
  Shield
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface BenefitPlan {
  id: string;
  name: string;
  description: string;
  category: string;
  monthlyPrice: string;
  dependentPrice: string;
  enrollmentFee: string;
  maxDependents: number;
  waitingPeriodDays: number;
  minContractMonths: number;
  requiresApproval: boolean;
  eligibleUserTypes: string[];
  subsidyType?: string;
  subsidyValue?: string;
  termsUrl?: string;
  coverageUrl?: string;
  networkUrl?: string;
  logoUrl?: string;
  bannerUrl?: string;
  template?: {
    id: string;
    name: string;
  };
}

const categoryIcons: Record<string, React.ReactNode> = {
  HEALTH: <Heart className="w-8 h-8" />,
  DENTAL: <Smile className="w-8 h-8" />,
  SUPERMARKET: <ShoppingBag className="w-8 h-8" />,
  GAS_STATION: <Fuel className="w-8 h-8" />,
  GIFT_CARD: <Gift className="w-8 h-8" />,
};

const categoryLabels: Record<string, string> = {
  HEALTH: 'Plano de Saúde',
  DENTAL: 'Plano Odontológico',
  SUPERMARKET: 'Rede de Supermercados',
  GAS_STATION: 'Rede de Postos',
  GIFT_CARD: 'Vale Presente',
  MEAL: 'Vale Refeição',
  PHARMACY: 'Farmácia',
  EDUCATION: 'Educação',
  WELLNESS: 'Bem-estar',
  OTHER: 'Outros',
};

export function BenefitDetails() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();

  const { data: plan, isLoading, error } = useQuery<BenefitPlan>({
    queryKey: ['benefit-plan', planId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.planDetails(planId!));
      return response.data;
    },
    enabled: !!planId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-700 rounded w-48 animate-pulse" />
        <Card className="animate-pulse">
          <div className="h-64 bg-surface-700 rounded" />
        </Card>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/banking/benefits')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Plano não encontrado
          </h3>
          <p className="text-surface-400">
            O plano solicitado não existe ou não está disponível
          </p>
        </Card>
      </div>
    );
  }

  const monthlyPrice = Number(plan.monthlyPrice) / 100;
  const dependentPrice = Number(plan.dependentPrice) / 100;
  const enrollmentFee = Number(plan.enrollmentFee) / 100;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/banking/benefits')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar ao Catálogo
      </Button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Info */}
        <Card className="flex-1">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
              {categoryIcons[plan.category] || <Gift className="w-8 h-8" />}
            </div>
            <div className="flex-1">
              <Badge variant="info" size="sm" className="mb-2">
                {categoryLabels[plan.category] || plan.category}
              </Badge>
              <h1 className="text-2xl font-bold text-white">{plan.name}</h1>
              {plan.template && (
                <p className="text-surface-400 text-sm mt-1">
                  Fornecido por {plan.template.name}
                </p>
              )}
            </div>
          </div>

          <p className="text-surface-300 mb-6">
            {plan.description || 'Sem descrição disponível'}
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface-800 rounded-lg p-4 text-center">
              <Users className="w-5 h-5 text-primary-400 mx-auto mb-2" />
              <p className="text-sm text-surface-400">Dependentes</p>
              <p className="text-white font-medium">
                {plan.maxDependents > 0 ? `Até ${plan.maxDependents}` : 'Não permite'}
              </p>
            </div>
            <div className="bg-surface-800 rounded-lg p-4 text-center">
              <Clock className="w-5 h-5 text-primary-400 mx-auto mb-2" />
              <p className="text-sm text-surface-400">Carência</p>
              <p className="text-white font-medium">
                {plan.waitingPeriodDays > 0 ? `${plan.waitingPeriodDays} dias` : 'Sem carência'}
              </p>
            </div>
            <div className="bg-surface-800 rounded-lg p-4 text-center">
              <Calendar className="w-5 h-5 text-primary-400 mx-auto mb-2" />
              <p className="text-sm text-surface-400">Contrato Mín.</p>
              <p className="text-white font-medium">
                {plan.minContractMonths > 0 ? `${plan.minContractMonths} meses` : 'Sem mínimo'}
              </p>
            </div>
            <div className="bg-surface-800 rounded-lg p-4 text-center">
              <Shield className="w-5 h-5 text-primary-400 mx-auto mb-2" />
              <p className="text-sm text-surface-400">Aprovação</p>
              <p className="text-white font-medium">
                {plan.requiresApproval ? 'Necessária' : 'Automática'}
              </p>
            </div>
          </div>

          {/* Documents */}
          {(plan.termsUrl || plan.coverageUrl || plan.networkUrl) && (
            <div className="border-t border-surface-700 pt-6">
              <h3 className="text-sm font-medium text-surface-400 mb-3">Documentos</h3>
              <div className="flex flex-wrap gap-2">
                {plan.termsUrl && (
                  <a
                    href={plan.termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-surface-800 rounded-lg text-surface-300 hover:text-white transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Termos de Uso
                  </a>
                )}
                {plan.coverageUrl && (
                  <a
                    href={plan.coverageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-surface-800 rounded-lg text-surface-300 hover:text-white transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Cobertura
                  </a>
                )}
                {plan.networkUrl && (
                  <a
                    href={plan.networkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-surface-800 rounded-lg text-surface-300 hover:text-white transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Rede Credenciada
                  </a>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Pricing Card */}
        <Card className="lg:w-80 h-fit">
          <h3 className="text-lg font-semibold text-white mb-4">Valores</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Mensalidade</span>
              <span className="text-2xl font-bold text-primary-400">
                {formatCurrency(monthlyPrice)}
              </span>
            </div>
            
            {plan.maxDependents > 0 && dependentPrice > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Por dependente</span>
                <span className="text-white font-medium">
                  +{formatCurrency(dependentPrice)}
                </span>
              </div>
            )}
            
            {enrollmentFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Taxa de adesão</span>
                <span className="text-white font-medium">
                  {formatCurrency(enrollmentFee)}
                </span>
              </div>
            )}

            {plan.subsidyType && plan.subsidyValue && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-400">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm font-medium">Subsídio da empresa</span>
                </div>
                <p className="text-green-300 mt-1">
                  {plan.subsidyType === 'PERCENTAGE'
                    ? `${Number(plan.subsidyValue) / 100}% de desconto`
                    : `${formatCurrency(Number(plan.subsidyValue) / 100)} de desconto`
                  }
                </p>
              </div>
            )}
          </div>

          <Button
            className="w-full"
            onClick={() => navigate(`/banking/benefits/subscribe/${plan.id}`)}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Fazer Adesão
          </Button>

          {plan.requiresApproval && (
            <p className="text-xs text-surface-500 text-center mt-3">
              Sua adesão será analisada pelo administrador
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

export default BenefitDetails;
